// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { never } from "grpc-web-rx"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timelineClient } from "../../../app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import {
  ClearRowTimesheetFieldRequest,
  ClearRowTimesheetFieldResponse,
  IdList,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { RootState } from "../../../app/store"
import { getTimesheetFieldLabel } from "../timesheet/timesheet-fields"

type ClearRowTimesheetFieldRpc = (request: ClearRowTimesheetFieldRequest) => Observable<ClearRowTimesheetFieldResponse>

export const clearRowTimesheetField = (clearRowTimesheetFieldRpc: ClearRowTimesheetFieldRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.clearRowTimesheetField.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => of(action.payload)
        .pipe(
          mergeMap(clearRowTimesheetFieldRpc),
          map(response => response.getTimeRowsList().map(timeRow => timeRow.toObject().id)),
          map((timeRowsIds) => Slice.clearRowTimesheetField({
            timeRowIds: new IdList().setIdsList(timeRowsIds),
            siteId: action.payload.getSiteId(),
            timesheetFieldId: action.payload.getTimesheetFieldId(),
          })),
          catchError(e => {
            const cacheKey = Slice.createTimesheetFieldCacheKey(
              action.payload.getSiteId(),
              action.payload.getTimesheetFieldId(),
            )
            const timesheetField = state.timeline.timesheetFieldsCache[cacheKey]
            const fieldName = timesheetField
              ? getTimesheetFieldLabel(timesheetField)
              : t({ id: "msg_timesheet_field", message: "Timesheet field" }).toLowerCase()
            return handleError(e, fieldName)
          }),
        )
      ),
    )

const handleError = (e: unknown, fieldName: string): Observable<Action> => {
  const actions: Action[] = []
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_clear_row_timesheet_field_not_found", 
            message: "Failed to clear {fieldName} value, {fieldName} not found.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(showSnackBar({
        message: t({ 
          id: "msg_clear_row_timesheet_field_permission_denied", 
          message: "Failed to clear {fieldName} value. You do not have permission.",
          values: { fieldName },
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_clear_row_timesheet_field_failed_precondition", 
            message: "Failed to clear {fieldName} value. Time row may be out of sync.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_clear_row_timesheet_field_failed",
      message: "Failed to clear {fieldName} value. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      values: { fieldName },
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const clearRowTimesheetFieldRpc = (request: ClearRowTimesheetFieldRequest): Observable<ClearRowTimesheetFieldResponse> =>
  grpc(() => timelineClient.clearRowTimesheetField(request, null), never)

export default clearRowTimesheetField(clearRowTimesheetFieldRpc)
